// deps
import { useState, useEffect, useContext, useRef, useMemo } from "react";

// internal-deps
import { None, Option, Some } from "@swiggy-private/js-fp";
import { useMountedRef } from "@swiggy-private/core-ui";
import { VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-core";

// interfaces
import { BankListData, GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";

// contexts
import {
    ApiContext,
    CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS,
    ConfigContext,
    PublicApiContext,
    useSessionStorage,
} from "@swiggy-private/checkout-core";
import { NetbankingMoreBanksContextValue } from "@payment-client/contexts/NetbankingMoreBanksContext";

// api
import { getBanksList } from "@payment-client/apis/Payment";

// helpers
import { getPaymentGroup } from "@payment-client/helpers/PaymentHelper";
import { isBusinessLineSwiggyMoney } from "@payment-client/helpers/Misc";
import { isPublicApiEnabled } from "@swiggy-private/core-utils";

// constants
import { NETBANKING_LIST } from "@payment-client/constants/Options";
import { SWIGGY_MONEY_MORE_BANKS_LIST_KEY, DEFAULT_MORE_BANKS_LIST_KEY } from "@payment-client/constants/StorageKeys";
import { getBanksListPublic } from "@payment-client/apis/publicPayment";

async function fetchMoreBanksList(
    apiHost: string,
    isPublicApiEnabledForMoreBanks: boolean,
): Promise<Option<BankListData>> {
    return isPublicApiEnabledForMoreBanks
        ? getBanksListPublic(apiHost).then(data => Some(data))
        : getBanksList(apiHost)
              .then(data => {
                  if (data == null) {
                      throw new Error("Received empty data from get banks list");
                  }
                  return Some(data);
              })
              .catch(err => {
                  __DEV__ || console.error(err);
                  return None as Option<BankListData>;
              });
}

const useMoreBanks = (
    isTurboPaymentMethod: boolean,
    paymentResponse?: GetPaymentMethodsResponse | null,
): NetbankingMoreBanksContextValue => {
    const apiContextValue = useContext(ApiContext);
    const { cachePaymentData } = useContext(ConfigContext);
    const apiCallRef = useRef(false);
    const isMountedRef = useMountedRef();
    const bankListKey = isBusinessLineSwiggyMoney() ? SWIGGY_MONEY_MORE_BANKS_LIST_KEY : DEFAULT_MORE_BANKS_LIST_KEY;
    const [dataInCache, saveDataInCache] = useSessionStorage<BankListData["bankList"] | null>(bankListKey, null);
    const [loading, setLoading] = useState(true);
    const [bankList, setBankList] = useState<BankListData["bankList"]>([]);

    const apiHost = apiContextValue.host.unwrap();
    const canCachePaymentData = cachePaymentData?.unwrapOr(true);
    const publicApiConfig = useContext(PublicApiContext);
    const isPublicApiEnabledForMoreBanks = useMemo(() => {
        return isPublicApiEnabled(
            publicApiConfig || {},
            CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.PAYMENT_APIS,
            CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.GET_BANKS_LIST,
        );
    }, [publicApiConfig]);

    useEffect(() => {
        // to skip get-all-banks api call in case of turbocheckout
        if (isTurboPaymentMethod || bankList.length > 0) {
            setLoading(false);
            return;
        }

        if (dataInCache && loading) {
            setLoading(false);
            setBankList(dataInCache);
            return;
        }

        const netbankingGroup = paymentResponse
            ? getPaymentGroup(paymentResponse, VALID_PAYMENT_GROUP_TYPES.NETBANKING)
            : undefined;
        if (netbankingGroup == null || !netbankingGroup.enabled || !loading || apiCallRef.current) {
            return;
        }

        apiCallRef.current = true;
        fetchMoreBanksList(apiHost, isPublicApiEnabledForMoreBanks)
            .then(BankListData => {
                apiCallRef.current = false;
                if (isMountedRef.current) {
                    const data = BankListData.isSome() ? BankListData.unwrap().bankList : null;

                    canCachePaymentData && saveDataInCache(data);
                    setLoading(false);
                    setBankList(data ?? NETBANKING_LIST);
                }
            })
            .catch(() => {
                apiCallRef.current = false;
                if (isMountedRef.current) {
                    canCachePaymentData && saveDataInCache(null);
                    setLoading(false);
                    // set with default list as fallback
                    setBankList(NETBANKING_LIST);
                }
            });
    }, [
        dataInCache,
        canCachePaymentData,
        apiHost,
        isMountedRef,
        saveDataInCache,
        paymentResponse,
        loading,
        bankList,
        isTurboPaymentMethod,
        isPublicApiEnabledForMoreBanks,
    ]);

    return { bankList };
};

export default useMoreBanks;
